import { Component, OnInit, OnDestroy, inject, effect, EffectRef } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { LanguageService } from './services/language.service';
import { UnhandledRejectionService } from './services/unhandled-rejection.service';
import { PushNotificationInitService } from './services/push-notification-init.service';
import { AuthService } from './services/auth.service';
import { ScreenReaderService } from './services/screen-reader.service';
import { Router } from '@angular/router';
import { OnboardingService } from './services/onboarding.service';
import { NicheService } from './services/niche.service';
import { DeepLinkService } from './services/deep-link.service';
import { AppStateService } from './services/app-state.service';
import { BillingService } from './services/billing.service';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import clarity from '@microsoft/clarity';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet, TranslocoPipe],
  styles: [`
    .app-loading-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: var(--ion-background-color);
      color: var(--ion-color-medium);
      font-family: var(--font-family, system-ui);
      gap: 1em;
    }
    .spinner {
      width: 40px;
      height: 40px;
      border: 4px solid var(--ion-color-light);
      border-top-color: var(--ion-color-primary);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    .loading-text {
      margin: 0;
      font-size: 1rem;
    }
  `],
})
export class AppComponent implements OnInit, OnDestroy {
  // ── Services ──
  private unhandledRejectionService = inject(UnhandledRejectionService);
  private pushInitService = inject(PushNotificationInitService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private onboardingService = inject(OnboardingService);
  private nicheService = inject(NicheService);
  private deepLinkService = inject(DeepLinkService);
  private billingService = inject(BillingService);
  private appState = inject(AppStateService);  // new hydration gate

  public screenReaderService = inject(ScreenReaderService);
  public languageService = inject(LanguageService);

  private deepLinkEffectRef!: EffectRef;
  private nicheHighStakesEffectRef!: EffectRef;
  private urlOpenListenerHandle: any;

  constructor() {
    this.applySavedTheme();

    // ═══════════════════════════════════════════════════════════
    //  Deep‑link consumption MUST wait for full hydration.
    // ═══════════════════════════════════════════════════════════
    this.deepLinkEffectRef = effect(() => {
      const loggedIn = this.auth.isLoggedIn();
      if (!loggedIn) return;

      // Block navigation until the app’s core data is loaded.
      if (!this.appState.isHydrated()) return;

      const pendingLink = this.deepLinkService.consumeDeepLink();
      if (pendingLink) {
        this.router.navigateByUrl(pendingLink, { replaceUrl: true });
      } else {
        // No stored deep link – trigger onboarding if needed.
        this.onboardingService.startIfNeeded();
      }
    });

    // ═══════════════════════════════════════════════════════════
    //  High‑stakes niche aesthetic toggling
    // ═══════════════════════════════════════════════════════════
    this.nicheHighStakesEffectRef = effect(() => {
      const isHighStakes = this.nicheService.isHighStakesNiche();
      if (isHighStakes) {
        document.documentElement.classList.add('niche-high-stakes');
      } else {
        document.documentElement.classList.remove('niche-high-stakes');
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  //  Hydration sequence:
  //   1. Language
  //   2. Deep‑link capture (before session restore)
  //   3. Session restore  ← CRITICAL, blocks navigation
  //   4. Sync locale
  //   5. Load niches (fresh fetch, essential data) – blocks context application
  //   6. Apply niche context
  //   7. Non‑critical billing load (fire‑and‑forget)
  //   8. Push init (non‑blocking)
  //   9. Future deep‑link listener
  //  10. Mark hydrated
  // ═══════════════════════════════════════════════════════════
  async ngOnInit(): Promise<void> {
    clarity.init('xlq01ri434'); 

    // ── 1. Initialize language service early ──
    await this.languageService.initLocale();

    // ── 2. Capture the launch URL (Universal Link / App Link) before auth restore ──
    if (Capacitor.isNativePlatform()) {
      try {
        const launch = await App.getLaunchUrl();
        if (launch?.url) {
          const internalPath = this.deepLinkService.normalizeUrl(launch.url);
          this.deepLinkService.setDeepLink(internalPath);
        }
      } catch {
        // Not critical
      }
    }

    // ── 3. Restore persistent session (CRITICAL – must complete before routing). ──
    await this.auth.restoreSession();

    // ── 4. Sync language with backend locale if authenticated ──
    if (this.auth.isLoggedIn()) {
      const backendLocale = this.auth.locale();
      const currentLang = this.languageService.currentLanguage();
      if (backendLocale && backendLocale !== currentLang) {
        await this.languageService.setLanguage(backendLocale);
      }
    }

    // ── 5. Load niches – essential for context application.
    //     Use a timeout to avoid an indefinite stall, but the context
    //     will be applied regardless of success.
    const HYDRATION_TIMEOUT_MS = 10_000;
    const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('hydration_timeout')), ms);
        promise
          .then(value => {
            clearTimeout(timer);
            resolve(value);
          })
          .catch(err => {
            clearTimeout(timer);
            reject(err);
          });
      });

    try {
      await withTimeout(this.nicheService.loadNiches(), HYDRATION_TIMEOUT_MS);
    } catch (err) {
      console.error('Niches loading failed or timed out', err);
    }

    // ── 6. Apply niche context – only now, after both session and niches are settled ──
    this.applyNicheContext();

    // ── 7. Billing status (non‑blocking, fire‑and‑forget) ──
    if (this.auth.isLoggedIn()) {
      this.billingService.loadStatus().catch(() => {});
    }

    // ── 8. Initialize OneSignal (native or web) – non‑blocking ──
    this.pushInitService.initialize().catch(error => {
      console.error('Push initialization failed:', error);
    });

    // ── 9. Listen for future deep links (app resumed from background, etc.) ──
    if (Capacitor.isNativePlatform()) {
      try {
        this.urlOpenListenerHandle = await App.addListener('appUrlOpen', (event: { url: string }) => {
          if (!event.url) return;
          const internalPath = this.deepLinkService.normalizeUrl(event.url);
          if (this.auth.isLoggedIn()) {
            this.router.navigateByUrl(internalPath, { replaceUrl: true });
          } else {
            // Store the link so it can be handled after the user logs in
            this.deepLinkService.setDeepLink(internalPath);
          }
        });
      } catch {
        // Not critical – proceed without cleaning up later (no handle to remove)
      }
    }

    // ──10. Signal that the app is fully hydrated (always called).
    //     This guarantees the UI unfreezes even when non‑critical APIs fail.
    this.appState.setHydrated();
  }

  ngOnDestroy(): void {
    this.deepLinkEffectRef.destroy();
    this.nicheHighStakesEffectRef?.destroy();
    if (this.urlOpenListenerHandle) {
      try {
        this.urlOpenListenerHandle.remove();
      } catch {
        // ignore
      }
    }
  }

  /**
   * Applies the active niche context **only after** the local session and
   * remote niches list are both settled.
   *
   * - Reads the persisted niche ID directly from AuthService.
   * - Validates it against the freshly loaded niches dataset.
   * - Falls back to the default niche ONLY if the persisted ID is
   *   missing, empty, or no longer present in the fetched list.
   */
  private applyNicheContext(): void {
    const nicheId = this.auth.nicheId();
    const niches = this.nicheService.availableNiches();

    if (nicheId) {
      const nicheExists = niches.some(n => n.id === nicheId);
      if (nicheExists) {
        this.nicheService.setCurrentNiche(nicheId);
        return;
      }
      // The persisted niche is no longer valid (e.g. deleted backend side)
      console.warn(`Persisted niche ID ${nicheId} not found in available niches, falling back to default.`);
    }

    // Safe fallback: default niche or first available
    const defaultNiche = niches.find(n => n.is_default) ?? niches[0];
    if (defaultNiche) {
      this.nicheService.setCurrentNiche(defaultNiche.id);
    }
  }

  private applySavedTheme(): void {
    // Default to dark mode unless user explicitly set it to 'false'.
    const isDarkMode = localStorage.getItem('darkModeOverride') !== 'false';
    const htmlElem = document.documentElement;
    htmlElem.classList.toggle('dark-mode', isDarkMode);
    htmlElem.classList.toggle('light-mode', !isDarkMode);
  }
}
